import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Platform, StyleSheet } from 'react-native'
import { ensurePushEnabled, getPushPermission, isPushGranted } from '@/modules/push'
import { useMutation, useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { convertUTCDataToUTCDate, getSchedulesForDay } from '@ppwr/sdk'

import { DS } from '../../design-system'
import { NotificationsDays } from './NotificationDays'
import { useStore } from '../../models'
import { observer } from 'mobx-react-lite'
import { NotificationSchedule } from './NotificationSchedule'
import { events } from '../../models/analytics/analytics-events'
import playTabConfig from '@/screens/play-screen/playTabConfig'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { BottomSheetScrollView } from '@gorhom/bottom-sheet'
import { numberDayToStringDayMap } from '@/components/Notifications/NotificationDay'
import { formatScheduleLocalTime } from '../../utils'

// Community schedules derive day-of-week from repeatDaysOfWeekUTC (legacy rows: startDayOfWeekUTC)
// and time-of-day from scheduledStartTime — no denormalized start* scalars are stored anymore.
function deriveScheduleTiming(schedule: {
  scheduledStartTime: number
  repeatDaysOfWeekUTC?: number[]
  startDayOfWeekUTC?: number
}): { startDayOfWeekUTC: number; startHoursUTC: number; startMinutesUTC: number } {
  const d = new Date(schedule.scheduledStartTime)
  const startDayOfWeekUTC =
    schedule.repeatDaysOfWeekUTC && schedule.repeatDaysOfWeekUTC.length > 0
      ? schedule.repeatDaysOfWeekUTC[0]
      : (schedule.startDayOfWeekUTC ?? 0)
  return { startDayOfWeekUTC, startHoursUTC: d.getUTCHours(), startMinutesUTC: d.getUTCMinutes() }
}

export const NotificationSheetContent = observer(({ dayOfWeek }: { dayOfWeek?: number }) => {
  const {
    permissions,
    analytics: { setEvent },
    permissions: { refresh, setIsPushDisabled },
    session: { user, updateUserPushNotificationConfig }
  } = useStore()

  const isPokerishEnabled = playTabConfig.modules.find(m => m.name === 'pokerish')?.enabled
  const insets = useSafeAreaInsets()

  const [currentDay, setCurrentDay] = useState(dayOfWeek ?? new Date().getDay())

  const userAuthId = user?._id ?? ''
  const publicSchedules = useQuery(api.gameSchedules.queries.getPublicDailySchedules)
  const togglePublicScheduleNotification = useMutation(api.gameSchedules.mutations.togglePublicScheduleNotification)

  const handlePermissionEvents = useCallback(async () => {
    // `undetermined` is the OS prompt still being available, which analytics calls 'default'.
    const status = await getPushPermission()
    const canRequestPermission = status === 'undetermined'

    setEvent(events.notificationSettings.notification_settings_screen_viewed, {
      user_id: user?._id,
      screen_name: 'notification_settings',
      native_notification_permission: canRequestPermission ? 'default' : status,
      native_notification_prompt_enabled: canRequestPermission
    })
  }, [user?._id, setEvent])

  useEffect(() => {
    handlePermissionEvents()
  }, [handlePermissionEvents])

  const isPushDisabled = permissions?.isPushDisabled
  const isGamePlayEnabled = user?.pushNotificationConfig?.gamePlay ?? false
  const isPokerishNotificationsEnabled = user?.pushNotificationConfig?.dailyPokerish ?? false

  /**
   * Filter the active public_daily schedules to those whose start time falls on the
   * currently selected day in the user's local timezone, then sort by local clock time.
   */
  const schedulesForCurrentDay = useMemo(() => {
    if (!publicSchedules) return []
    return publicSchedules
      .filter((schedule) => getSchedulesForDay(deriveScheduleTiming(schedule), currentDay as ReturnType<Date['getUTCDay']>))
      .map((schedule) => {
        const timing = deriveScheduleTiming(schedule)
        return {
          schedule,
          localStart: convertUTCDataToUTCDate(
            timing.startDayOfWeekUTC,
            timing.startHoursUTC,
            timing.startMinutesUTC
          ).getTime()
        }
      })
      .sort((a, b) => a.localStart - b.localStart)
      .map(({ schedule }) => schedule)
  }, [publicSchedules, currentDay])

  const toggleNotification = async (scheduleId: string, value: boolean) => {
    const hasPermission = await isPushGranted()
    setIsPushDisabled(!hasPermission)
    if (!hasPermission) {
      ensurePushEnabled(value)
      return
    }
    if (!userAuthId) return

    const scheduleData = schedulesForCurrentDay.find((sch) => sch._id === scheduleId)

    try {
      await togglePublicScheduleNotification({
        scheduleId: scheduleId as Id<'gameSchedules'>,
        userId: userAuthId
      })
    } catch (error) {
      console.error('togglePublicScheduleNotification error', error)
      return
    }

    if (scheduleData) {
      const timing = deriveScheduleTiming(scheduleData)
      setEvent(events.notificationSettings.notification_toggle_community_game_updated, {
        user_id: user?._id,
        new_state: value ? 'on' : 'off',
        time: formatScheduleLocalTime(timing.startDayOfWeekUTC, timing.startHoursUTC, timing.startMinutesUTC),
        day_of_week: numberDayToStringDayMap[currentDay]
      })
    }
  }

  const toggleGamePlayNotification = async (id: string, value: boolean) => {
    const hasPermission = await isPushGranted()
    setIsPushDisabled(!hasPermission)

    if (!hasPermission) {
      ensurePushEnabled(value)
      return
    }

    setEvent(events.notificationSettings.notification_toggle_general_updated, {
      user_id: user?._id,
      new_state: value ? 'on' : 'off'
    })

    const pushConfig = {
      dailyPokerish: isPokerishNotificationsEnabled,
      gamePlay: isGamePlayEnabled
    }

    updateUserPushNotificationConfig({ ...pushConfig, gamePlay: value })
  }

  const toggleDailyPokerishNotification = async (id: string, value: boolean) => {
    const hasPermission = await isPushGranted()
    setIsPushDisabled(!hasPermission)

    if (!hasPermission) {
      ensurePushEnabled(value)
      return
    }

    const pushConfig = {
      dailyPokerish: isPokerishNotificationsEnabled,
      gamePlay: isGamePlayEnabled
    }

    updateUserPushNotificationConfig({ ...pushConfig, dailyPokerish: value })
  }

  const onPressDay = (day: number) => {
    setCurrentDay(day)
  }

  useEffect(() => {
    refresh()
  }, [refresh])

  return (
    <BottomSheetScrollView
      contentContainerStyle={{ paddingBottom: Platform.OS === 'web' ? 300 : insets.bottom + 32 }}
      style={{ backgroundColor: DS.palette['Brand/Cream'] }}
    >
      <DS.Frame style={styles.container}>
        <DS.Text variant="Heading 2/Medium" color="Global/Black" style={{ marginVertical: 24 }}>
          Push Notifications
        </DS.Text>

        <DS.Frame gap="24">
          <NotificationSchedule
            key={'game-play-notification'}
            _id={'game-play-notification'}
            enabled={isPushDisabled ? false : isGamePlayEnabled}
            title={'General'}
            subtitle="Game Play"
            toggleNotification={toggleGamePlayNotification}
            isPermissionEnabled={!isPushDisabled}
          />
          {isPokerishEnabled && (
            <NotificationSchedule
              key={'daily-pokerish-notification'}
              _id={'dailyy-pokerish-notification'}
              enabled={isPushDisabled ? false : isPokerishNotificationsEnabled}
              title={'Daily Pokerish'}
              subtitle=""
              toggleNotification={toggleDailyPokerishNotification}
              isPermissionEnabled={!isPushDisabled}
            />
          )}
        </DS.Frame>

        <DS.Frame style={{ height: 1, marginVertical: 24 }} bg="Brand/Light Grey" />
        <DS.Frame style={{ marginBottom: 24 }}>
          <DS.Text variant="Heading 5/Regular" color="Global/Black">
            Community Game Alerts
          </DS.Text>
        </DS.Frame>

        <NotificationsDays onPress={onPressDay} currentDay={currentDay} days={[0, 1, 2, 3, 4, 5, 6]} />

        <DS.Frame gap="24" style={{ marginTop: 24 }}>
          {schedulesForCurrentDay.map((sch) => {
            const isPushEnabled = sch.usersToNotify?.includes(userAuthId) ?? false
            const timing = deriveScheduleTiming(sch)
            const title = formatScheduleLocalTime(timing.startDayOfWeekUTC, timing.startHoursUTC, timing.startMinutesUTC)
            return (
              <NotificationSchedule
                key={sch._id.toString()}
                _id={sch._id.toString()}
                enabled={isPushDisabled ? false : isPushEnabled}
                title={title}
                toggleNotification={toggleNotification}
                isPermissionEnabled={!isPushDisabled}
              />
            )
          })}
        </DS.Frame>
      </DS.Frame>
    </BottomSheetScrollView>
  )
})

const styles = StyleSheet.create({
  rootContainer: {
    flex: 1
  },
  container: {
    flex: 1,
    backgroundColor: '#F6F2E9',
    paddingHorizontal: 32
  },
  title: {
    fontSize: 25,
    textAlign: 'center',
    color: '#121212'
  }
})
